import { DOCUMENT, isPlatformBrowser, ViewportScroller } from '@angular/common';
import { inject, Injectable, PLATFORM_ID, provideAppInitializer } from '@angular/core';
import { NavigationEnd, NavigationStart, Router } from '@angular/router';
import { filter } from 'rxjs/operators';

const FOOTER_THRESHOLD_PX = 50;

@Injectable({ providedIn: 'root' })
export class ScrollRestorationService {
  private readonly router = inject(Router);
  private readonly viewportScroller = inject(ViewportScroller);
  private readonly document = inject(DOCUMENT);
  private readonly platformId = inject(PLATFORM_ID);

  private readonly footerScroll = new Map<string, [number, number]>();
  private isBack = false;
  private ready = false;

  init(): void {
    if (!isPlatformBrowser(this.platformId)) return;

    history.scrollRestoration = 'auto';

    this.router.events
      .pipe(filter((e) => e instanceof NavigationStart || e instanceof NavigationEnd))
      .subscribe((e) => {
        if (e instanceof NavigationStart) {
          this.isBack = e.navigationTrigger === 'popstate';
          if (this.isBack) {
            history.scrollRestoration = this.footerScroll.has(e.url) ? 'manual' : 'auto';
            return;
          }
          const url = this.router.url;
          if (this.isNearFooter()) {
            this.footerScroll.set(url, this.viewportScroller.getScrollPosition());
          } else {
            this.footerScroll.delete(url);
          }
          return;
        }

        if (!this.ready) {
          this.ready = true;
        } else if (!this.isBack) {
          setTimeout(() => this.viewportScroller.scrollToPosition([0, 0]));
        }

        if (this.isBack) {
          this.restoreFooter(e.urlAfterRedirects);
        }
      });
  }

  private restoreFooter(url: string): void {
    const pos = this.footerScroll.get(url);
    if (!pos) return;

    setTimeout(() => {
      this.viewportScroller.scrollToPosition(pos);
      history.scrollRestoration = 'auto';
    });
  }

  private isNearFooter(): boolean {
    const [, y] = this.viewportScroller.getScrollPosition();
    const { scrollHeight, clientHeight } = this.document.documentElement;
    return y + clientHeight >= scrollHeight - FOOTER_THRESHOLD_PX;
  }
}

export function provideScrollRestoration() {
  return provideAppInitializer(() => inject(ScrollRestorationService).init());
}
